import { useConsole } from "contexts/Console"
import { useEnv } from "contexts/Env"
import { useLocale } from "contexts/Locale"
import { useNavigation } from "contexts/Navigation"
import { usePage } from "contexts/Page"
import { Helmet } from "react-helmet-async"
import { useLocation } from "react-router-dom"

const META_MAP = new Map([
  ["ogtitle", "og:title"],
  ["ogdescription", "og:description"],
  ["ogurl", "og:url"],
  ["ogimage", "og:image"],
  ["ogsearchimage", "og:searchimage"],
  ["ogtype", "og:type"],
  ["ogsitename", "og:sitename"],
  ["searchSection", "search_section"],
  ["searchTitle", "search_title"],
  ["searchExclude", "search_exclude"],
])

const GOVERNANCE_SLUG = "/about-rolex/sustainable-development/sustainability-report/governance"

function isOpenGraphMetaName(name) {
  return name.startsWith("og:")
}

function getMetaName(key) {
  return META_MAP.has(key) ? META_MAP.get(key) : key
}

function getOrganizationUrl(localeCode) {
  return localeCode === "en" ? "https://www.rolex.com" : `https://www.rolex.com/${localeCode}`
}

function getPersonStructuredData({ initialState, localeCode, env, includeContext = false }) {
  return {
    ...(includeContext && { "@context": "https://schema.org" }),
    "@type": "Person",
    name: initialState?.structured_data?.person_name,
    jobTitle: initialState?.structured_data?.jobtitle,
    worksFor: getOrganizationStructuredData({ localeCode, env }),
  }
}

function getOrganizationStructuredData({ localeCode }) {
  const organizationUrl = getOrganizationUrl(localeCode)
  return {
    "@type": "Organization",
    name: "Rolex SA",
    url: organizationUrl,
  }
}

function getHomepageStructuredData({ initialState, localeCode, env }) {
  return {
    "@context": "https://schema.org",
    ...getOrganizationStructuredData({ localeCode }),
    logo: `${new URL("f_auto/q_auto/c_limit,w_300/rolexcom/rolex-logo.png", `https://${env.cloudinary.secureDistribution}`)}`,
    employee: getPersonStructuredData({ initialState, localeCode, env }),
    sameAs: [
      "https://www.youtube.com/rolex",
      "https://www.instagram.com/rolex/",
      "https://www.threads.com/@rolex",
      "https://www.facebook.com/rolex",
      "https://www.linkedin.com/company/rolex",
      "https://x.com/ROLEX",
      "https://www.pinterest.com/rolex/",
      "https://weibo.com/rolex",
      "https://v.douyin.com/uo9k_ivcQTQ/",
      "https://lin.ee/uGN7jOr1",
    ],
  }
}

function getGovernanceStructuredData({ initialState, localeCode, env }) {
  return getPersonStructuredData({
    initialState,
    localeCode,
    env,
    includeContext: true,
  })
}

export function populateMetaCS(data) {
  const head = process.browser ? document.documentElement.querySelector("head") : null
  if (!head) return
  ;[...Object.entries(data)]?.forEach(([k, v]) => {
    if (k === "title") {
      const el = head.querySelector(k)
      if (el) el.innerHTML = v
    } else {
      const metaName = getMetaName(k)
      const el = head.querySelector(`meta[${isOpenGraphMetaName(metaName) ? "property" : "name"}="${metaName}"]`)
      if (el) el.setAttribute("content", v)
    }
  })
}

function populateMetaSS(data) {
  return [...Object.entries(data)]?.map(([k, v]) => {
    if (k === "lngpref") return null
    if (k === "title") return <title key={k}>{v}</title>
    if (!v) return null

    const metaName = getMetaName(k)
    return isOpenGraphMetaName(metaName) ? <meta key={k} property={metaName} content={v} /> : <meta key={k} name={metaName} content={v} />
  })
}

function cleanMeta(data) {
  //  console.log("++++ cleanMeta", data)
  data = data ?? {}
  return [...Object.entries(data)].reduce((a, [k, v]) => {
    if (v) a[k] = v
    return a
  }, {})
}

function initMeta(page_meta, locale_meta, href, lng_prefix, env) {
  //  console.log("++++ initMeta", page_meta, locale_meta, href, lng_prefix, env)
  page_meta = page_meta ?? {}
  locale_meta = locale_meta ?? {}

  function fillUrl(v, lng_prefix) {
    const u = new URL(`${lng_prefix}${v}`, `https://${env.hostname}`).href
    //    if (u[u.length - 1] === "/") u = u.slice(0, -1)
    return u
  }

  let robots
  if (page_meta.noindexing) robots = "noindex"

  return Object.assign(
    {
      title: page_meta.title,
      description: page_meta.description,
      image: page_meta.image,
      searchimage: page_meta.searchimage,
      ogtitle: page_meta.title_og || page_meta.title,
      ogdescription: page_meta.description_og || page_meta.description,
      ogurl: href || fillUrl(page_meta.url, lng_prefix),
      ogimage:
        page_meta.image_og_cld &&
        new URL(page_meta.image_og_cld.replace("/rolexcom", "/q_auto/f_auto/rolexcom"), `https://${env.cloudinary.secureDistribution}`),
      ogsearchimage:
        page_meta.searchimage_og_cld &&
        new URL(page_meta.searchimage_og_cld.replace("/rolexcom", "/q_auto/f_auto/rolexcom"), `https://${env.cloudinary.secureDistribution}`),
      ogtype: locale_meta.type_og,
      ogsitename: locale_meta.sitename_og,
      searchSection: page_meta.search_section,
      searchTitle: page_meta.search_title,
      searchExclude: page_meta.search_exclude === true ? "true" : undefined,
      lngpref: lng_prefix,
    },
    robots && { robots }
  )
}

export function updateMeta(param, value, query_meta, lngpref, env) {
  const query_meta_config_param = query_meta?.find(qm => qm.param === param)
  //  console.log("++++ updateMeta query_meta_config_param", query_meta_config_param)
  if (!query_meta_config_param) return null
  const query_meta_param_value = query_meta_config_param.values.find(qmv => qmv.value === value)
  //  console.log("++++ updateMeta query_meta_param_value", query_meta_param_value)
  if (!query_meta_param_value) return null
  return cleanMeta(initMeta(query_meta_param_value, null, null, lngpref, env))
}

export default function Meta({ children, initialState, ctx }) {
  const console = useConsole()
  const location = useLocation()
  const page = usePage()
  const locale = useLocale()
  const navigation = useNavigation()
  const env = useEnv()

  let _pathname = location.pathname
  const serviceCentreDetect = "/rolex-dealers/service/rswi_"
  const serviceCentreFix = "/rolex-dealers/rswi_"
  if (_pathname.includes(serviceCentreDetect)) {
    _pathname = _pathname.replace(serviceCentreDetect, serviceCentreFix)
  }

  const u = new URL(_pathname, `https://${env.hostname}`)
  let href = u.href
  if (href[href.length - 1] === "/") href = href.slice(0, -1)
  let pathname = u.pathname
  if (pathname[pathname.length - 1] !== "/") pathname = `${pathname}/`
  let lng_prefix = pathname.match(/^\/[a-z]{2}([-_][a-zA-Z]{2,4})?\//) ?? ["/"]
  lng_prefix = lng_prefix[0].slice(0, -1)
  //  console.verbose("lng_prefix", lng_prefix)

  ctx.initialMeta = initMeta(page?.meta, initialState?.[locale.current.codes.www]?.meta, href, lng_prefix, env)
  const query_meta = initialState?.[locale.current.codes.www]?.query_meta
  const query_meta_param = page?.query_meta_param ?? []

  const getPageType = () => {
    return ctx?.page_type
  }
  const pageType = getPageType()
  const slugWithoutTrailingSlash = navigation.slug.replace(/\/$/, "")
  const isGovernancePage = slugWithoutTrailingSlash === GOVERNANCE_SLUG

  let virtualMeta = {}
  for (const param of query_meta_param) {
    const searchParamsValue = [...new URLSearchParams(location.search)].find(([k, v]) => k === param)
    virtualMeta = updateMeta(param, searchParamsValue?.[1], query_meta, lng_prefix, env) ?? {}
  }
  virtualMeta = Object.assign({}, ctx.initialMeta, virtualMeta)

  let canonical =
    !["wishlist page"].includes(page?.page_type) && // some pages do not have a canonical ?
    (() => {
      if (page?.content_in_english) {
        // CSR pages
        if (env.cn && locale.current.codes.www === "zh-hans") {
          const url = new URL(href)
          url.pathname = "/en" + url.pathname
          return url.href
        } else if (!env.cn && locale.current.codes.www !== "fr") {
          const url = new URL(href)
          url.pathname = url.pathname.replace(/^\/[a-z]{2}([-_][a-zA-Z]{2,4})?\//, "/")
          return url.href
        } else {
          return href
        }
      } else {
        //        switch (locale.current.codes.www) {
        //          case "zh-hans": {
        // if zh-hans, force .cn with no locale code in url
        //            const url = new URL(href)
        //            url.hostname = "www.rolex.cn"
        //            url.pathname = url.pathname.replace(/^\/zh-hans/, "")
        //            return url.href
        //          }
        // case "zh-hant": { /** zh-hant is not present anymore on rolex.cn */
        //   // if zh-hant, force .cn
        //   const url = new URL(href)
        //   url.hostname = "rolex.cn"
        //   return url.href
        // }
        //          default:
        return href
        //        }
      }
    })()

  if (page.meta.canonical) {
    canonical = new URL(navigation.localize(page.meta.canonical, locale.current.codes.www), `https://${env.hostname}`)
  }

  const alternates = /*    !env.cn && locale.current.codes.www === "zh-hans" // No alternates for zh-hans on .com, only canonical (pointing to .cn)
      ? []
      : */ [
    { hrefLang: "x-default", code: locale.defaultLocale.codes.www },
    { hrefLang: "en-cn", code: "en", external: !env.cn },
    ...[
      ...locale.locales
        .filter(({ meta }) => meta)
        .map(({ codes: { www: code }, external }) => ({
          hrefLang: code,
          code,
          external,
        })),
    ],
  ]
    .map(({ hrefLang, code, external }) => {
      let pathname = navigation.localize(navigation.slug, code)
      const origin = env.cn && (external || code === "zh-hans-cn" || hrefLang === "zh-hans") ? "https://www.rolex.com" : `https://${env.hostname}` // TODO cn parity with current env
      if (env.cn && code === "zh-hans" && origin === "https://www.rolex.com") pathname = `${code}${navigation.slug}`

      let href
      try {
        href = new URL(pathname, origin).href
      } catch {
        href = new URL(pathname, "https://www.rolex.com").href
      }

      if (!env.cn && locale.current.codes.www !== "zh-hant-tw" && hrefLang === "en-cn") {
        const url = new URL(href)
        url.hostname = "www.rolex.cn"
        if (hrefLang === "zh-hans") url.pathname = url.pathname.replace(/^\/zh-hans/, "")
        if (hrefLang === "en-cn") url.pathname = `/en${url.pathname}`
        href = url.href
      }
      if (!env.cn && locale.current.codes.www === "zh-hant-tw" && (hrefLang === "zh-hans-cn" || hrefLang === "en-cn")) {
        return null
      }

      if (env.cn && hrefLang === "en") {
        const url = new URL(href)
        url.hostname = "www.rolex.com"
        url.pathname = url.pathname.replace(/^\/en/, "")
        href = url.href
      }

      if (hrefLang === "zh-hans-cn") {
        const url = new URL(href)
        url.hostname = "www.rolex.cn"
        url.pathname = url.pathname.replace(/^\/zh-hans-cn/, "")
        href = url.href
      }

      if (href[href.length - 1] === "/") href = href.slice(0, -1)

      return { hrefLang, href }
    })
    .filter(Boolean)

  const structuredData =
    pageType === "homepage"
      ? getHomepageStructuredData({
          initialState,
          localeCode: locale.current.codes.www,
          env,
        })
      : isGovernancePage
      ? getGovernanceStructuredData({
          initialState,
          localeCode: locale.current.codes.www,
          env,
        })
      : null

  console.verbose("Meta(%o)", ctx.initialMeta)
  return (
    <>
      <Helmet>
        <html data-status={page.meta.title === "404" ? "404" : "200"} />
        {populateMetaSS(virtualMeta)}
        {canonical && <link rel='canonical' href={canonical} />}
        {alternates.map(({ hrefLang, href }) => (
          <link key={hrefLang} rel='alternate' hrefLang={hrefLang} href={href} />
        ))}
        {structuredData && <script type='application/ld+json'>{JSON.stringify(structuredData)}</script>}
      </Helmet>
      {children}
    </>
  )
}
